import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Box, Button, TextField, CircularProgress, Autocomplete,
  FormControl, InputLabel, Select, MenuItem, FormHelperText,
  Alert, Chip, Tooltip, Typography, Divider, alpha, useTheme,
} from '@mui/material';
import { createFilterOptions } from '@mui/material/Autocomplete';
import {
  AddCircleOutline as CreateIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { debounce } from 'lodash';
import wineNameService, {
  ProducerOption, RegionOption, GrapeOption,
  WineColor, WineType, WineSubType, WineName,
} from '../../services/wineNameService';
import wineService, { parseVintageInput, Wine } from '../../services/wineService';

// ── filter factories ──────────────────────────────────────────────────────────
const filterProducers = createFilterOptions<ProducerOption>();
const filterRegions = createFilterOptions<RegionOption>();
const filterGrapes = createFilterOptions<GrapeOption>();

// ── label maps ────────────────────────────────────────────────────────────────
// Insertion order is the canonical wine-color order — see WINE_COLOR_ORDER in
// utils/wineColor. Driven via Object.entries(...) into the Color select below.
const colorLabels: Record<WineColor, string> = {
  SPARKLING: 'Sparkling', WHITE: 'White', ORANGE: 'Orange', ROSE: 'Rosé',
  RED: 'Red', DESSERT: 'Dessert', FORTIFIED: 'Fortified',
};

const typeLabels: Record<WineType, string> = {
  WINE: 'Wine', SPIRITS: 'Spirits', BEER: 'Beer', OTHER: 'Other',
};

const subTypeLabels: Record<WineSubType, string> = {
  STILL: 'Still', SPARKLING: 'Sparkling', FORTIFIED: 'Fortified',
  DESSERT: 'Dessert', SWEET: 'Sweet', NATURAL: 'Natural',
};

// ── props ─────────────────────────────────────────────────────────────────────
interface Props {
  open: boolean;
  initialQuery: string;
  onCreated: (wine: Wine) => void;
  onClose: () => void;
}

// ── component ─────────────────────────────────────────────────────────────────
const CreateWineModal: React.FC<Props> = ({ open, initialQuery, onCreated, onClose }) => {
  const theme = useTheme();

  // ── form state ────────────────────────────────────────────────────────────
  const [producer, setProducer] = useState<ProducerOption | null>(null);
  const [producerInputValue, setProducerInputValue] = useState('');
  const [region, setRegion] = useState<RegionOption | null>(null);
  const [labelGrapes, setLabelGrapes] = useState<GrapeOption[]>([]);
  const [specialName, setSpecialName] = useState('');
  const [color, setColor] = useState<WineColor | ''>('');
  const [wineType, setWineType] = useState<WineType>('WINE');
  const [subType, setSubType] = useState<WineSubType | ''>('');
  const [vintageInput, setVintageInput] = useState('');

  // ── autocomplete options ──────────────────────────────────────────────────
  const [producerOptions, setProducerOptions] = useState<ProducerOption[]>([]);
  const [regionOptions, setRegionOptions] = useState<RegionOption[]>([]);
  const [grapeOptions, setGrapeOptions] = useState<GrapeOption[]>([]);

  // ── loading / status ──────────────────────────────────────────────────────
  const [loadingProducers, setLoadingProducers] = useState(false);
  const [loadingRegions, setLoadingRegions] = useState(false);
  const [loadingGrapes, setLoadingGrapes] = useState(false);
  const [creatingOnFly, setCreatingOnFly] = useState(false);
  const [saving, setSaving] = useState(false);

  // ── validation errors ─────────────────────────────────────────────────────
  const [errors, setErrors] = useState<{
    producer?: string; region?: string; color?: string; vintage?: string;
  }>({});

  // ── duplicate warnings (non-blocking) ────────────────────────────────────
  const [wineNameDuplicate, setWineNameDuplicate] = useState<WineName | null>(null);
  const [wineDuplicateWarning, setWineDuplicateWarning] = useState(false);
  const [submitError, setSubmitError] = useState('');

  // ── prefill producer input from initialQuery on open ─────────────────────
  const prevOpen = useRef(false);
  useEffect(() => {
    if (open && !prevOpen.current) {
      // Reset form
      setProducer(null);
      setProducerInputValue(initialQuery);
      setRegion(null);
      setLabelGrapes([]);
      setSpecialName('');
      setColor('');
      setWineType('WINE');
      setSubType('');
      setVintageInput('');
      setProducerOptions([]);
      setRegionOptions([]);
      setGrapeOptions([]);
      setErrors({});
      setWineNameDuplicate(null);
      setWineDuplicateWarning(false);
      setSubmitError('');

      // Kick off producer search with the prefilled query
      if (initialQuery.trim().length >= 2) {
        setLoadingProducers(true);
        wineNameService.searchProducers(initialQuery).then(opts => {
          setProducerOptions(opts);
        }).finally(() => setLoadingProducers(false));
      }
    }
    prevOpen.current = open;
  }, [open, initialQuery]);

  // ── debounced searches ────────────────────────────────────────────────────
  const searchProducers = useMemo(
    () => debounce(async (q: string) => {
      setLoadingProducers(true);
      try { setProducerOptions(await wineNameService.searchProducers(q)); }
      finally { setLoadingProducers(false); }
    }, 300),
    []
  );

  const searchRegions = useMemo(
    () => debounce(async (q: string) => {
      setLoadingRegions(true);
      try { setRegionOptions(await wineNameService.searchRegions(q)); }
      finally { setLoadingRegions(false); }
    }, 300),
    []
  );

  const searchGrapes = useMemo(
    () => debounce(async (q: string) => {
      setLoadingGrapes(true);
      try { setGrapeOptions(await wineNameService.searchGrapes(q)); }
      finally { setLoadingGrapes(false); }
    }, 300),
    []
  );

  // ── duplicate check for WineName ──────────────────────────────────────────
  const dupCheckTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (!producer) { setWineNameDuplicate(null); return; }
    if (dupCheckTimer.current) clearTimeout(dupCheckTimer.current);
    dupCheckTimer.current = setTimeout(async () => {
      try {
        const result = await wineNameService.checkDuplicate({
          producerId: producer._id,
          regionId: region?._id,
          specialName: specialName.trim() || undefined,
          labelGrapeIds: labelGrapes.map(g => g._id),
        });
        setWineNameDuplicate(result.isDuplicate && result.existing ? result.existing : null);
      } catch { /* ignore */ }
    }, 600);
  }, [producer, region, specialName, labelGrapes]);

  // ── create-on-the-fly handlers ────────────────────────────────────────────
  const handleProducerChange = useCallback(async (_: any, newValue: ProducerOption | null) => {
    if (!newValue) { setProducer(null); return; }
    if (newValue.__isNew__) {
      setCreatingOnFly(true);
      try {
        const created = await wineNameService.createProducerOnFly(newValue.inputValue!);
        setProducer(created);
        setProducerOptions(prev => [created, ...prev]);
      } catch { setSubmitError('Failed to create producer. Please try again.'); }
      finally { setCreatingOnFly(false); }
    } else {
      setProducer(newValue);
    }
    setErrors(prev => ({ ...prev, producer: undefined }));
  }, []);

  const handleRegionChange = useCallback(async (_: any, newValue: RegionOption | null) => {
    if (!newValue) { setRegion(null); return; }
    if (newValue.__isNew__) {
      setCreatingOnFly(true);
      try {
        const created = await wineNameService.createRegionOnFly(newValue.inputValue!);
        setRegion(created);
        setRegionOptions(prev => [created, ...prev]);
      } catch { setSubmitError('Failed to create region. Please try again.'); }
      finally { setCreatingOnFly(false); }
    } else {
      setRegion(newValue);
    }
    setErrors(prev => ({ ...prev, region: undefined }));
  }, []);

  const handleGrapesChange = useCallback(async (_: any, newValues: GrapeOption[]) => {
    if (newValues.length > 3) return;
    const last = newValues[newValues.length - 1];
    if (last?.__isNew__) {
      setCreatingOnFly(true);
      try {
        const created = await wineNameService.createGrapeOnFly(last.inputValue!);
        setLabelGrapes([...newValues.slice(0, -1), created]);
        setGrapeOptions(prev => [created, ...prev]);
      } catch { setSubmitError('Failed to create grape. Please try again.'); }
      finally { setCreatingOnFly(false); }
    } else {
      setLabelGrapes(newValues);
    }
  }, []);

  // ── validation ────────────────────────────────────────────────────────────
  const validate = (): boolean => {
    const newErrors: typeof errors = {};
    if (!producer) newErrors.producer = 'Producer is required';
    if (!region) newErrors.region = 'Region is required';
    if (!color) newErrors.color = 'Color is required';
    if (!vintageInput.trim()) {
      newErrors.vintage = 'Vintage is required';
    } else {
      const parsed = parseVintageInput(vintageInput);
      if (!parsed.valid) newErrors.vintage = parsed.error ?? 'Invalid vintage';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // ── submit ────────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    if (!validate()) return;
    setSaving(true);
    setSubmitError('');
    setWineDuplicateWarning(false);

    try {
      // 1. Create WineName (needsReview: true)
      const wineName = await wineNameService.create({
        producerId: producer!._id,
        regionId: region!._id,
        labelGrapeIds: labelGrapes.map(g => g._id),
        specialName: specialName.trim() || undefined,
        color: color as WineColor,
        type: wineType,
        subType: subType || undefined,
        active: true,
        needsReview: true,
      });

      // 2. Parse vintage
      const parsed = parseVintageInput(vintageInput);

      // 3. Check Wine duplicate (warn only)
      try {
        const dupCheck = await wineService.checkDuplicate({
          wineNameId: wineName._id,
          vintageType: parsed.vintageType,
          vintage: parsed.vintage,
        });
        if (dupCheck.isDuplicate) setWineDuplicateWarning(true);
      } catch { /* ignore — non-blocking */ }

      // 4. Create Wine (needsReview: true)
      const wine = await wineService.create({
        wineNameId: wineName._id,
        vintageType: parsed.vintageType,
        vintage: parsed.vintage,
        baseVintage: parsed.baseVintage,
        active: true,
        needsReview: true,
      });

      onCreated(wine);
    } catch (err) {
      console.error('Failed to create wine:', err);
      setSubmitError('Failed to create. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const isBusy = saving || creatingOnFly;

  // ── render ────────────────────────────────────────────────────────────────
  return (
    <Dialog open={open} onClose={isBusy ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 600 }}>Create New Wine</DialogTitle>

      <DialogContent dividers>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, pt: 0.5 }}>

          {submitError && (
            <Alert severity="error" sx={{ mb: 1 }}>{submitError}</Alert>
          )}

          {wineNameDuplicate && (
            <Alert severity="warning" sx={{ mb: 1 }}>
              A similar wine name already exists: <strong>{wineNameDuplicate.displayName}</strong>.
              You can still proceed — it will be flagged for review.
            </Alert>
          )}

          {wineDuplicateWarning && (
            <Alert severity="warning" sx={{ mb: 1 }}>
              A wine with this name and vintage may already exist. It will be flagged for review.
            </Alert>
          )}

          {creatingOnFly && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <CircularProgress size={14} />
              <Typography variant="caption" color="text.secondary">Creating…</Typography>
            </Box>
          )}

          {/* Producer */}
          <Autocomplete<ProducerOption>
            options={producerOptions}
            value={producer}
            inputValue={producerInputValue}
            loading={loadingProducers}
            isOptionEqualToValue={(o, v) => o._id === v._id}
            getOptionLabel={o => o.__isNew__ ? o.name : (o.fullName || o.name)}
            filterOptions={(options, params) => {
              const filtered = filterProducers(options, params);
              const { inputValue } = params;
              const exists = options.some(o => (o.fullName || o.name).toLowerCase() === inputValue.toLowerCase());
              if (inputValue !== '' && !exists) {
                filtered.push({ _id: '__new__', name: `Create "${inputValue}"`, __isNew__: true, inputValue });
              }
              return filtered;
            }}
            onInputChange={(_, val) => {
              setProducerInputValue(val);
              if (val.trim().length >= 2) searchProducers(val);
            }}
            onChange={handleProducerChange}
            renderOption={(props, option) => (
              <li {...props} key={option._id}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {option.__isNew__ && <CreateIcon fontSize="small" color="primary" />}
                  <Box>
                    <Typography variant="body2" fontWeight={option.__isNew__ ? 600 : 400}>
                      {option.__isNew__ ? option.name : (option.fullName || option.name)}
                    </Typography>
                    {option.needsReview && !option.__isNew__ && (
                      <Typography variant="caption" color="warning.main">Needs review</Typography>
                    )}
                  </Box>
                </Box>
              </li>
            )}
            renderInput={params => (
              <TextField
                {...params}
                label="Producer"
                required
                margin="normal"
                error={Boolean(errors.producer)}
                helperText={errors.producer || 'Search or type to create'}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: <>{loadingProducers && <CircularProgress size={16} />}{params.InputProps.endAdornment}</>,
                }}
              />
            )}
          />

          {/* Region */}
          <Autocomplete<RegionOption>
            options={regionOptions}
            value={region}
            loading={loadingRegions}
            isOptionEqualToValue={(o, v) => o._id === v._id}
            getOptionLabel={o => o.name}
            filterOptions={(options, params) => {
              const filtered = filterRegions(options, params);
              const { inputValue } = params;
              const exists = options.some(o => o.name.toLowerCase() === inputValue.toLowerCase());
              if (inputValue !== '' && !exists) {
                filtered.push({ _id: '__new__', name: `Create "${inputValue}"`, __isNew__: true, inputValue });
              }
              return filtered;
            }}
            onInputChange={(_, val) => { if (val.trim().length >= 2) searchRegions(val); }}
            onChange={handleRegionChange}
            renderOption={(props, option) => (
              <li {...props} key={option._id}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {option.__isNew__ && <CreateIcon fontSize="small" color="primary" />}
                  <Box>
                    <Typography variant="body2" fontWeight={option.__isNew__ ? 600 : 400}>{option.name}</Typography>
                    {!option.__isNew__ && option.hierarchyNames && option.hierarchyNames.length > 1 && (
                      <Typography variant="caption" color="text.secondary">
                        {option.hierarchyNames.join(' › ')}
                      </Typography>
                    )}
                    {option.needsReview && !option.__isNew__ && (
                      <Typography variant="caption" color="warning.main">Needs review</Typography>
                    )}
                  </Box>
                </Box>
              </li>
            )}
            renderInput={params => (
              <TextField
                {...params}
                label="Region"
                required
                margin="normal"
                error={Boolean(errors.region)}
                helperText={errors.region || 'Search or type to create'}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: <>{loadingRegions && <CircularProgress size={16} />}{params.InputProps.endAdornment}</>,
                }}
              />
            )}
          />

          {/* Label Grapes */}
          <Autocomplete<GrapeOption, true>
            multiple
            options={grapeOptions}
            value={labelGrapes}
            loading={loadingGrapes}
            isOptionEqualToValue={(o, v) => o._id === v._id}
            getOptionLabel={o => o.primaryName}
            filterOptions={(options, params) => {
              const filtered = filterGrapes(options, params);
              const { inputValue } = params;
              const exists = options.some(o => o.primaryName.toLowerCase() === inputValue.toLowerCase());
              if (inputValue !== '' && !exists && labelGrapes.length < 3) {
                filtered.push({ _id: '__new__', primaryName: `Create "${inputValue}"`, __isNew__: true, inputValue });
              }
              return filtered;
            }}
            onInputChange={(_, val) => { if (val.trim().length >= 2) searchGrapes(val); }}
            onChange={handleGrapesChange}
            getOptionDisabled={() => labelGrapes.length >= 3}
            renderOption={(props, option) => (
              <li {...props} key={option._id}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {option.__isNew__ && <CreateIcon fontSize="small" color="primary" />}
                  <Box>
                    <Typography variant="body2" fontWeight={option.__isNew__ ? 600 : 400}>
                      {option.primaryName}
                    </Typography>
                    {!option.__isNew__ && option.color && (
                      <Typography variant="caption" color="text.secondary">
                        {option.color.replace('_', ' / ')}
                      </Typography>
                    )}
                    {option.needsReview && !option.__isNew__ && (
                      <Typography variant="caption" color="warning.main">Needs review</Typography>
                    )}
                  </Box>
                </Box>
              </li>
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => {
                const { key, ...other } = getTagProps({ index });
                return (
                  <Chip
                    key={key}
                    label={option.primaryName}
                    size="small"
                    icon={option.needsReview
                      ? <Tooltip title="Needs review"><WarningIcon sx={{ fontSize: '14px !important' }} /></Tooltip>
                      : undefined}
                    {...other}
                  />
                );
              })
            }
            renderInput={params => (
              <TextField
                {...params}
                label="Label Grapes"
                margin="normal"
                helperText={labelGrapes.length >= 3 ? 'Maximum 3 grapes' : 'Optional — up to 3'}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: <>{loadingGrapes && <CircularProgress size={16} />}{params.InputProps.endAdornment}</>,
                }}
              />
            )}
          />

          {/* Special Name */}
          <TextField
            fullWidth
            label="Special Name"
            value={specialName}
            onChange={e => setSpecialName(e.target.value)}
            margin="normal"
            helperText='Optional — cuvée or vineyard name'
          />

          <Divider sx={{ my: 1 }} />

          {/* Color + Type */}
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mt: 1 }}>
            <FormControl required error={Boolean(errors.color)}>
              <InputLabel>Color</InputLabel>
              <Select
                value={color}
                onChange={e => {
                  setColor(e.target.value as WineColor);
                  setErrors(prev => ({ ...prev, color: undefined }));
                }}
                label="Color"
              >
                {(Object.entries(colorLabels) as [WineColor, string][]).map(([val, label]) => (
                  <MenuItem key={val} value={val}>{label}</MenuItem>
                ))}
              </Select>
              {errors.color && <FormHelperText>{errors.color}</FormHelperText>}
            </FormControl>

            <FormControl>
              <InputLabel>Type</InputLabel>
              <Select
                value={wineType}
                onChange={e => setWineType(e.target.value as WineType)}
                label="Type"
              >
                {(Object.entries(typeLabels) as [WineType, string][]).map(([val, label]) => (
                  <MenuItem key={val} value={val}>{label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* Sub Type */}
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Sub Type</InputLabel>
            <Select
              value={subType}
              onChange={e => setSubType(e.target.value as WineSubType | '')}
              label="Sub Type"
            >
              <MenuItem value="">— None —</MenuItem>
              {(Object.entries(subTypeLabels) as [WineSubType, string][]).map(([val, label]) => (
                <MenuItem key={val} value={val}>{label}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <Divider sx={{ my: 2 }} />

          {/* Vintage */}
          <TextField
            fullWidth
            label="Vintage"
            required
            value={vintageInput}
            onChange={e => {
              setVintageInput(e.target.value);
              setErrors(prev => ({ ...prev, vintage: undefined }));
            }}
            error={Boolean(errors.vintage)}
            helperText={errors.vintage || 'e.g. 2018, NV, MV'}
            placeholder="2018"
          />

          <Alert severity="info" sx={{ mt: 2, fontSize: '0.78rem' }}>
            Both the Wine Name and Wine will be flagged <strong>Needs Review</strong> — visible in the admin review queue.
          </Alert>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Button onClick={onClose} disabled={isBusy} sx={{ textTransform: 'none' }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={isBusy}
          sx={{ textTransform: 'none', fontWeight: 600, minWidth: 120 }}
        >
          {saving ? <CircularProgress size={18} color="inherit" /> : 'Create & Select'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateWineModal;
